import React, { useEffect, useRef } from 'react';
import LDAPSearchField from '../../../app/components/shared/LDAPSearch/LDAPSearchField';
import type { SlcTeamContact } from '../../../app/services/momentsApi';
import { MILESTONE_REVIEW_TYPE } from '../../../utils/constants';
import { isApprovalFlowStatus } from '../../../utils/approvalFlow';
import './EditMilestoneModal.css';

/**
 * A contact as understood by {@link LDAPSearchField} — at minimum a display name,
 * usually with a `uid`/`email`. Kept loose to stay compatible with the modal's
 * existing approver shapes.
 */
export interface SendForApprovalContact {
  uid?: string;
  name: string;
  email?: string;
  mail?: string;
  [key: string]: any;
}

/**
 * Whether the Send for Approval form applies for the given review type + status.
 *
 * Only for Async milestones in an approval-flow status. The `sent` status is the
 * entry point, so the checkbox is always available there. For the downstream
 * statuses (Sent - Feedback Pending / Approved / Re-Approved) the form is shown
 * only when the `sendForApproval` flag is already checked. Reuses
 * {@link isApprovalFlowStatus} so the form and the flow indicator stay in sync.
 *
 * `approvalRequired` is the resolved value from `getMilestoneApprovalRequired()` /
 * `milestoneAttributes.approvalRequired` — when it's explicitly `false` (e.g. RR1,
 * RR2, MKTG PKG, Media Brainstorm), there's no approver to send the request to, so
 * the whole Send-for-Approval flow never applies, regardless of status. Leave it
 * `undefined` and the check is skipped (existing behavior for unresolved milestones).
 */
export function shouldShowSendForApprovalForm(
  reviewType?: string,
  status?: string,
  sendForApproval = false,
  approvalRequired?: boolean
): boolean {
  if (
    reviewType !== MILESTONE_REVIEW_TYPE.ASYNC ||
    !isApprovalFlowStatus(status) ||
    approvalRequired === false
  ) {
    return false;
  }
  // Entry point: always show at `sent`; otherwise gate on the checked flag.
  return (status || '').toLowerCase() === 'sent' || sendForApproval;
}

export interface SendForApprovalFormProps {
  /**
   * Disables the checkbox until the milestone form above is valid (all mandatory
   * fields filled — dates, approver, links, etc.). Defaults to false.
   */
  disabled?: boolean;
  /**
   * Read-only display mode. The form is only editable at the `sent` status; for
   * the downstream statuses (Pending / Approved / Re-Approved) it is shown
   * read-only. Defaults to false.
   */
  readOnly?: boolean;
  /** Controlled state of the "Send for Approval" checkbox. */
  sendForApproval: boolean;
  onSendForApprovalChange: (checked: boolean) => void;
  /** Approvers chosen in the Approver field above — rendered as readonly TO chips. */
  approvers: SendForApprovalContact[];
  /** Fixed SLC-team Cc recipients every approval-request email Cc's regardless of what the
   *  user picks below — one entry per configured address/group (milestone-approval.slc-team-cc
   *  may list several), each shown as its own locked chip in CC so they're visible before Send
   *  for Review is ever submitted. Undefined until GET default-approvers has resolved. */
  slcTeamCc?: SlcTeamContact[];
  /** CC recipients (editable, same picker as Approver). */
  cc: SendForApprovalContact[];
  onCcChange: (cc: SendForApprovalContact[]) => void;
  /** Prepopulated subject from the milestone data. Readonly — the user cannot edit it. */
  subject: string;
  /** Prepopulated note. Editable by the user. */
  note: string;
  onNoteChange: (note: string) => void;
}

/** cssClasses reused across the modal's LDAP pickers so CC matches Approver exactly. */
const LDAP_CSS_CLASSES = {
  ldapFieldContainer: 'figma-ldap-field-container',
  pickerContainer: 'figma-ldap-picker-container',
  ldapTextField: 'figma-ldap-text-field',
  selectedTag: 'figma-ldap-selected-tag',
  tagRemoveButton: 'figma-ldap-tag-remove-button',
  ldapDropdown: 'figma-ldap-dropdown'
} as const;

/**
 * "Send for Approval" form shown at the end of the Edit Milestone modal for
 * Async milestones in an approval-sending status (e.g. Sent). The parent owns
 * all state; this component only renders and reports changes.
 */
const SendForApprovalForm: React.FC<SendForApprovalFormProps> = ({
  disabled = false,
  readOnly = false,
  sendForApproval,
  onSendForApprovalChange,
  approvers,
  slcTeamCc = [],
  cc,
  onCcChange,
  subject,
  note,
  onNoteChange
}) => {
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const previousSendForApprovalRef = useRef(sendForApproval);

  useEffect(() => {
    const wasChecked = previousSendForApprovalRef.current;
    previousSendForApprovalRef.current = sendForApproval;

    if (sendForApproval && !wasChecked && !readOnly) {
      noteRef.current?.focus();
    }
  }, [sendForApproval, readOnly]);

  // Every SLC-team recipient is Cc'd on the approval-request email regardless of what the
  // user picks — each shown as a locked, non-removable chip so that's visible up front, but
  // never forwarded to the parent's onCcChange: the backend already adds them separately, so
  // including them here too would double them up in the actual PATCH/email payload. Tagged
  // with __isSlcTeam (not just a sentinel uid) since resolved individuals DO have a real uid.
  const slcTeamContacts: SendForApprovalContact[] = slcTeamCc.map(
    (contact, index) => ({
      uid: contact.uid || `__slc-team-${index}__`,
      name: contact.name,
      email: contact.uid ? undefined : contact.name,
      __isSlcTeam: true
    })
  );
  const ccWithSlcTeam = [...slcTeamContacts, ...cc];
  const handleCcChange = (selected: SendForApprovalContact[]) => {
    onCcChange((selected || []).filter(c => !c.__isSlcTeam));
  };

  return (
    <>
      <div className="figma-separator" />
      <div
        className="figma-send-approval-section"
        data-testid="send-for-approval-section"
      >
        {/* Checkbox — always visible; gates the rest of the form.
            Disabled until the milestone form above is valid. */}
        <label
          className={`figma-send-approval-checkbox-row${
            disabled ? ' is-disabled' : ''
          }`}
          title={
            disabled && !readOnly
              ? 'Complete the required fields above to enable'
              : undefined
          }
        >
          <input
            type="checkbox"
            className="figma-send-approval-checkbox"
            checked={sendForApproval}
            disabled={disabled}
            onChange={e => onSendForApprovalChange(e.target.checked)}
            data-testid="send-for-approval-checkbox"
          />
          <span className="figma-send-approval-checkbox-label">
            Send for Approval
          </span>
        </label>

        {sendForApproval && (
          <div className="figma-send-approval-body">
            {/* TO — readonly chips mirroring the Approver field above */}
            <div className="figma-form-field">
              <div className="figma-to-header">
                <span className="figma-field-label">TO:</span>
                <span className="figma-to-helper">
                  {slcTeamCc.length > 0
                    ? '(Email will be sent to the Approver listed above and the Cc recipients shown below)'
                    : '(Email will be sent to the Approver listed above)'}
                </span>
              </div>
              <div
                className="figma-to-chips"
                data-testid="send-for-approval-to"
              >
                {approvers.map((approver, index) => (
                  <span
                    key={approver.uid || approver.name || index}
                    className="figma-send-approval-to-chip"
                    title={approver.name}
                  >
                    {approver.name}
                  </span>
                ))}
              </div>
            </div>

            {/* CC (editable) */}
            <div className="figma-form-field figma-cc-column">
              <label className="figma-field-label">CC:</label>
              {readOnly ? (
                <div className="figma-cc-readonly">
                  {/* Greyed-out, non-interactive field to mirror the editable
                      CC picker; the selected CCs are listed as chips below. */}
                  <div
                    className="figma-ldap-picker-container figma-cc-readonly-field"
                    aria-disabled="true"
                  >
                    <span className="figma-cc-readonly-placeholder">
                      Start Typing for Users
                    </span>
                  </div>
                  {ccWithSlcTeam.length > 0 && (
                    <div
                      className="figma-to-chips figma-cc-readonly-chips"
                      data-testid="send-for-approval-cc"
                    >
                      {ccWithSlcTeam.map((contact, index) => (
                        <span
                          key={contact.uid || contact.name || index}
                          className="figma-send-approval-to-chip"
                          title={contact.name}
                        >
                          {contact.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="figma-ldap-wrapper">
                  <LDAPSearchField
                    label=""
                    value={ccWithSlcTeam}
                    onChange={handleCcChange}
                    isRequired={false}
                    cssClasses={LDAP_CSS_CLASSES}
                    searchMode="users-and-groups"
                    isChipRemoveDisabled={contact => !!contact.__isSlcTeam}
                    chipRemoveDisabledTitle="This address is always Cc'd automatically"
                  />
                </div>
              )}
            </div>

            {/* SUBJECT (readonly) — full-width textarea so the whole subject
                line is readable instead of being clipped in a single-line input */}
            <div className="figma-form-field">
              <label className="figma-field-label">SUBJECT</label>
              <textarea
                className="figma-note-textarea figma-readonly-input"
                value={subject}
                readOnly
                data-testid="send-for-approval-subject"
              />
            </div>

            {/* NOTE TO APPROVER (editable) */}
            <div className="figma-form-field">
              <label className="figma-field-label">NOTE TO APPROVER</label>
              <textarea
                ref={noteRef}
                className={`figma-note-textarea${
                  readOnly ? ' figma-readonly-input' : ''
                }`}
                value={note}
                onChange={e => onNoteChange(e.target.value)}
                readOnly={readOnly}
                data-testid="send-for-approval-note"
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SendForApprovalForm;
